import { apiClient } from '@/shared/utils/api';
import { Payment, CreatePaymentRequest, PaginatedResponse } from '@/shared/types/api';

class PaymentService {
  private readonly baseUrl = '/payments';

  public async getPayments(params?: {
    visit_id?: string;
    payment_method?: string;
    status?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<Payment>> {
    const response = await apiClient.get<PaginatedResponse<Payment>>(this.baseUrl, {
      params,
    });
    return response.data;
  }

  public async getPayment(id: string): Promise<Payment> {
    const response = await apiClient.get<Payment>(`${this.baseUrl}/${id}/`);
    return response.data;
  }

  public async createPayment(data: CreatePaymentRequest): Promise<Payment> {
    const response = await apiClient.post<Payment>(`${this.baseUrl}/`, data);
    return response.data;
  }

  public async refundPayment(id: string, refund_amount: string, reason?: string, reference_number?: string): Promise<Payment> {
    const payload: any = {
      refund_amount,
      reason,
    };

    // Only include reference_number if it's provided
    if (reference_number) {
      payload.reference_number = reference_number;
    }

    const response = await apiClient.post<Payment>(`${this.baseUrl}/${id}/refund`, payload);
    return response.data;
  }

  public async calculateTestRefund(payment_id: string): Promise<{
    payment_id: string;
    visit_number: string;
    patient_name: string;
    original_amount: number;
    already_refunded: number;
    total_tests: number;
    completed_tests: number;
    uncompleted_tests: number;
    uncompleted_test_details: Array<{
      test_name: string;
      test_code: string;
      status: string;
      price: number;
    }>;
    uncompleted_tests_amount: number;
    suggested_refund_amount: number;
    maximum_refundable: number;
  }> {
    const response = await apiClient.get(`${this.baseUrl}/${payment_id}/calculate-test-refund`);
    return response.data;
  }

  public async getRefundHistory(payment_id: string): Promise<Array<{
    id: string;
    refund_id: string;
    refund_amount: number;
    reason: string;
    reference_number: string | null;
    processed_by_name: string | null;
    approved_by_name: string | null;
    suggested_amount: number | null;
    processed_at: string;
  }>> {
    const response = await apiClient.get(`${this.baseUrl}/${payment_id}/refund-history`);
    return response.data;
  }

  public async getPaymentSummary(visitId: string): Promise<{
    total_amount: string;
    paid_amount: string;
    pending_amount: string;
    refunded_amount: string;
    payments: Payment[];
  }> {
    const response = await apiClient.get(`/visits/${visitId}/payment-summary/`);
    return response.data;
  }

  public async getDailyReport(date?: string): Promise<{
    total_collections: string;
    payment_method_breakdown: Record<string, string>;
    refunds: string;
    net_collections: string;
    transaction_count: number;
  }> {
    const response = await apiClient.get(`${this.baseUrl}/daily-report/`, {
      params: { date },
    });
    return response.data;
  }

  // Utility methods
  public getStatusColor(status: Payment['status']): string {
    const colors = {
      Pending: 'bg-yellow-100 text-yellow-800',
      Success: 'bg-green-100 text-green-800',
      Failed: 'bg-red-100 text-red-800',
      Refunded: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }

  public getMethodColor(method: Payment['payment_method']): string {
    const colors = {
      Cash: 'bg-green-100 text-green-800',
      Card: 'bg-blue-100 text-blue-800',
      UPI: 'bg-purple-100 text-purple-800',
      Insurance: 'bg-orange-100 text-orange-800',
    };
    return colors[method] || 'bg-gray-100 text-gray-800';
  }

  public formatAmount(amount: string | number): string {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  public formatPaymentReference(reference: string, method: Payment['payment_method']): string {
    const prefix = {
      Cash: 'CASH',
      Card: 'CARD',
      UPI: 'UPI',
      Insurance: 'INS',
    }[method] || '';

    return reference.startsWith(prefix) ? reference : `${prefix}_${reference}`;
  }

  public canRefund(payment: Payment): boolean {
    return payment.status === 'Success' && payment.payment_method !== 'Insurance';
  }

  public validateCardNumber(cardNumber: string): boolean {
    // Basic Luhn algorithm for card validation
    const cleaned = cardNumber.replace(/\D/g, '');
    if (cleaned.length < 13 || cleaned.length > 19) return false;

    let sum = 0;
    let isEven = false;

    for (let i = cleaned.length - 1; i >= 0; i--) {
      let digit = parseInt(cleaned[i]);

      if (isEven) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  }

  public validateUPIId(upiId: string): boolean {
    const upiRegex = /^[a-zA-Z0-9.-]{2,256}@[a-zA-Z0-9.-]{2,64}$/;
    return upiRegex.test(upiId);
  }

  public getMaskedCardNumber(cardNumber: string): string {
    const cleaned = cardNumber.replace(/\D/g, '');
    if (cleaned.length < 4) return cardNumber;

    const last4 = cleaned.slice(-4);
    const masked = '*'.repeat(cleaned.length - 4);
    return `${masked}${last4}`;
  }

  public calculateChange(amountPaid: number, totalAmount: number): number {
    return Math.max(0, amountPaid - totalAmount);
  }

  public splitPaymentMethods(
    _totalAmount: number,
    methods: { method: Payment['payment_method']; amount: number }[]
  ): CreatePaymentRequest[] {
    return methods
      .filter(({ amount }) => amount > 0)
      .map(({ method, amount }) => ({
        visit_id: '', // Will be filled by caller
        payment_method: method,
        amount: amount.toString(),
      }));
  }

  public generateReceiptData(payment: Payment) {
    return {
      receiptNumber: payment.payment_reference,
      date: new Date(payment.created_at).toLocaleDateString('en-IN'),
      time: new Date(payment.created_at).toLocaleTimeString('en-IN'),
      amount: this.formatAmount(payment.amount),
      method: payment.payment_method,
      status: payment.status,
      transactionRef: payment.transaction_reference,
    };
  }

  public async getRefundApprovals(): Promise<{
    approvals: Array<{
      id: string;
      approval_id: string;
      payment_reference: string;
      visit_number: string;
      patient_name: string;
      refund_amount: number;
      suggested_amount: number;
      reason: string;
      reference_number: string;
      status: 'Pending' | 'Approved' | 'Rejected';
      requested_by_name: string;
      requested_at: string;
      reviewed_by_name: string | null;
      reviewed_at: string | null;
      review_notes: string;
    }>;
    total: number;
  }> {
    const response = await apiClient.get(`${this.baseUrl}/refund-approvals`);
    return response.data;
  }

  public async getRefundApprovalsCount(): Promise<number> {
    const response = await apiClient.get(`${this.baseUrl}/refund-approvals`, {
      params: { count_only: 'true' }
    });
    return response.data.count;
  }

  public async reviewRefundApproval(
    approval_id: string,
    action: 'approve' | 'reject',
    review_notes?: string
  ): Promise<{
    message: string;
    approval: any;
    payment?: any;
  }> {
    const response = await apiClient.post(
      `${this.baseUrl}/refund-approvals/${approval_id}/review`,
      { action, review_notes }
    );
    return response.data;
  }
}

export const paymentService = new PaymentService();
export default paymentService;
